import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { motion } from "framer-motion";
import { db, doc, updateDoc } from "../../../../firebaseConfig";

import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";

const PrefixSelector = ({ jobData, setHasUnsavedChanges }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [eventPrefix, setEventPrefix] = useState(jobData.eventPrefix);
  const [eventPrefixCustom, setEventPrefixCustom] = useState(
    jobData.eventPrefixCustom
  );

  useEffect(() => {
    setEventPrefix(jobData.eventPrefix);
    setEventPrefixCustom(jobData.eventPrefixCustom);
  }, [jobData]);

  const handleCustomPrefixChange = (e) => {
    if (!isAdmin) return;
    setEventPrefixCustom(e.target.value);
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const handleCustomPrefixFocus = () => {
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const handleCustomPrefixBlur = () => {
    if (!isAdmin || eventPrefixCustom === jobData.eventPrefixCustom) {
      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(false);
      }
      return;
    }

    updateDoc(doc(db, "JobData", jobData.id), { eventPrefixCustom: eventPrefixCustom })
      .then(() => {
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  const filterButtonStyles = {
    fontWeight: "bold",
    padding: "8px 12px",
    minWidth: { xs: "auto", sm: "70px" },
    border: `1px solid ${
      theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
    } !important`,
    borderRadius: "8px !important",
    fontSize: { xs: 12, sm: 13 },
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "none",
    boxShadow:
      theme === "dark"
        ? "0 2px 5px rgba(0,0,0,0.2)"
        : "0 2px 5px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap",
  };

  const handleChange = (e) => {
    if (!isAdmin) return;
    let val = e.target.value;
    if (e.target.value !== "Custom") {
      setEventPrefixCustom("");
    }
    if (e.target.value === "clear") val = "";

    setEventPrefix(val);
    updateDoc(doc(db, "JobData", jobData.id), { eventPrefix: val });
  };

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <motion.div
              variants={itemVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <ToggleButtonGroup
                value={eventPrefix || ""}
                onChange={handleChange}
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                  gap: 1,
                }}
              >
                <ToggleButton
                  value="AM"
                  size="small"
                  sx={filterButtonStyles}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  AM
                </ToggleButton>
                <ToggleButton
                  value="PM"
                  size="small"
                  sx={filterButtonStyles}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  PM
                </ToggleButton>
                <ToggleButton
                  value="Custom"
                  size="small"
                  sx={filterButtonStyles}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Custom
                </ToggleButton>
                <ToggleButton
                  value="clear"
                  size="small"
                  sx={filterButtonStyles}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Clear
                </ToggleButton>
              </ToggleButtonGroup>
            </motion.div>

            {eventPrefix === "Custom" && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <TextField
                  variant="filled"
                  placeholder="Custom prefix"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                      },
                    },
                  }}
                  size="small"
                  sx={{ width: 150 }}
                  value={eventPrefixCustom || ""}
                  onChange={handleCustomPrefixChange}
                  onFocus={handleCustomPrefixFocus}
                  onBlur={handleCustomPrefixBlur}
                />
              </motion.div>
            )}
          </Box>
        </motion.div>
      </Box>
    </FormControl>
  );
};

export default PrefixSelector;
